import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTrophy, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { generateBrackets, advanceWinner } from '../utils/tournamentUtils.js';
import { useAuth } from '../context/AuthContext.js';
import { getServices } from '../firebase.js';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

/**
 * TournamentBracket Component
 * 
 * @param {Object} props
 * @param {Array} props.brackets - The tournament brackets data
 * @param {string} props.format - Tournament format (single-elimination, double-elimination, etc.)
 * @param {Array} props.teams - Array of team data for the tournament
 * @param {boolean} props.loading - Loading state
 * @param {Array} props.participants - Array of participant IDs (used if brackets not provided)
 * @param {string} props.tournamentId - ID of the tournament
 * @param {Object} props.tournament - Tournament data
 * @param {Function} props.onBracketsUpdate - Callback function when brackets are updated
 */
const TournamentBracket = ({ 
  brackets = [], 
  format = 'single-elimination', 
  teams = [], 
  loading = false,
  participants = [],
  tournamentId,
  tournament,
  onBracketsUpdate
}) => {
  const [rounds, setRounds] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');
  const [updatingMatch, setUpdatingMatch] = useState(false);
  const [error, setError] = useState(null);
  
  const { currentUser } = useAuth();
  const { db } = getServices();
  
  // Check if user has permission to edit matches
  const canEditMatches = () => {
    if (!currentUser || !tournament) return false;
    
    return (
      // User is tournament creator
      tournament.creatorId === currentUser.uid ||
      // User is an admin
      (tournament.admins && tournament.admins.includes(currentUser.uid))
    );
  };
  
  // Process bracket data into rounds for display
  useEffect(() => {
    if (brackets && brackets.length > 0) {
      // Sort brackets by round number
      const sortedBrackets = [...brackets].sort((a, b) => a.round - b.round);
      
      // Group matches by round
      const processedRounds = sortedBrackets.map(bracket => ({
        round: bracket.round,
        title: getRoundTitle(bracket.round, sortedBrackets.length, format),
        matches: bracket.matches || []
      }));
      
      setRounds(processedRounds);
    } else if (participants && participants.length > 0) {
      // If we have participants but no brackets, generate them
      const generatedBrackets = generateBrackets(format, findPowerOfTwo(participants.length));
      
      // Process the generated brackets
      const processedRounds = generatedBrackets.map(bracket => ({
        round: bracket.round,
        title: getRoundTitle(bracket.round, generatedBrackets.length, format),
        matches: bracket.matches || []
      }));
      
      setRounds(processedRounds);
    } else {
      // Create mock rounds for display if no data is provided
      createMockBracket();
    }
  }, [brackets, format, participants]);
  
  // Find the next power of 2 that is greater than or equal to n
  const findPowerOfTwo = (n) => {
    return Math.pow(2, Math.ceil(Math.log2(n)));
  };
  
  // Generate round title based on round number and format
  const getRoundTitle = (roundNum, totalRounds, format) => {
    if (format === 'single-elimination' || format === 'double-elimination') {
      if (roundNum === totalRounds) {
        return 'Finals';
      } else if (roundNum === totalRounds - 1) {
        if (format === 'double-elimination') {
          return 'Semi-Finals';
        } else {
          return 'Semi-Finals';
        }
      } else if (roundNum === totalRounds - 2) {
        return 'Quarter-Finals';
      } else {
        return `Round ${roundNum}`;
      }
    } else if (format === 'round-robin') {
      return `Round ${roundNum}`;
    } else {
      return `Round ${roundNum}`;
    }
  };
  
  // Create mock bracket for preview
  const createMockBracket = () => {
    // For a single elimination tournament with 8 teams
    const mockRounds = [
      {
        round: 1,
        title: 'Round 1',
        matches: [
          { id: 1, team1: 'Team Alpha', team2: 'Team Beta', score: '2-0', winner: 'Team Alpha' },
          { id: 2, team1: 'Team Charlie', team2: 'Team Delta', score: '2-1', winner: 'Team Charlie' },
          { id: 3, team1: 'Team Echo', team2: 'Team Foxtrot', score: '0-2', winner: 'Team Foxtrot' },
          { id: 4, team1: 'Team Golf', team2: 'Team Hotel', score: '2-0', winner: 'Team Golf' }
        ]
      },
      {
        round: 2,
        title: 'Semi-Finals',
        matches: [
          { id: 5, team1: 'Team Alpha', team2: 'Team Charlie', score: '2-1', winner: 'Team Alpha' },
          { id: 6, team1: 'Team Foxtrot', team2: 'Team Golf', score: '1-2', winner: 'Team Golf' }
        ]
      },
      {
        round: 3,
        title: 'Finals',
        matches: [
          { id: 7, team1: 'Team Alpha', team2: 'Team Golf', score: '3-2', winner: 'Team Alpha' }
        ]
      }
    ];
    
    setRounds(mockRounds);
  };
  
  // Get team name or placeholder
  const getTeamName = (teamId) => {
    if (!teamId) return 'TBD';
    
    // If teams array is provided, look up the team name
    if (teams && teams.length > 0) {
      const team = teams.find(t => t.id === teamId);
      return team ? team.name : teamId;
    }
    
    return teamId; // Return the ID if no team is found
  };
  
  // Handle starting to edit a match
  const handleEditMatch = (match, roundIndex, matchIndex) => {
    setEditingMatch({
      match,
      roundIndex,
      matchIndex
    });
    
    // Pre-fill the current scores if available
    if (match.score) {
      const [score1, score2] = match.score.split('-');
      setScore1(score1 || '0');
      setScore2(score2 || '0');
    } else {
      setScore1('0');
      setScore2('0');
    }
    
    setError(null);
  };
  
  // Handle canceling match edit
  const handleCancelEdit = () => {
    setEditingMatch(null);
    setScore1('');
    setScore2('');
    setError(null);
  };
  
  // Handle saving match result
  const handleSaveMatch = async () => {
    if (!editingMatch) return;
    
    // Validate scores
    const s1 = parseInt(score1);
    const s2 = parseInt(score2);
    
    if (isNaN(s1) || isNaN(s2) || s1 < 0 || s2 < 0) {
      setError('Please enter valid scores (non-negative numbers)');
      return;
    }
    
    // Determine winner
    let winner = null;
    if (s1 > s2) {
      winner = editingMatch.match.team1;
    } else if (s2 > s1) {
      winner = editingMatch.match.team2;
    } else {
      setError('Match cannot end in a tie. Please enter different scores.');
      return;
    }
    
    if (!tournamentId) {
      setError('Tournament ID is missing');
      return;
    }
    
    // Get tournament reference
    const tournamentRef = db.collection('tournaments').doc(tournamentId);
    
    try {
      setUpdatingMatch(true);
      setError(null);
      
      // Start a transaction to update the brackets
      await db.runTransaction(async (transaction) => {
        // Get the tournament document
        const tournamentDoc = await transaction.get(tournamentRef);
        
        if (!tournamentDoc.exists) {
          throw new Error('Tournament not found');
        }
        
        // Get the current brackets
        const tournamentData = tournamentDoc.data();
        const currentBrackets = tournamentData.brackets || [];
        
        // Create updated brackets
        const updatedBrackets = [...currentBrackets];
        
        // Update the match
        const roundData = updatedBrackets[editingMatch.roundIndex];
        if (roundData && roundData.matches) {
          roundData.matches[editingMatch.matchIndex] = {
            ...editingMatch.match,
            score: `${s1}-${s2}`,
            winner,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedBy: currentUser.uid
          };
        }
        
        // If we have a winner, advance to the next round
        if (winner && format !== 'round-robin') {
          advanceWinner(
            updatedBrackets, 
            editingMatch.roundIndex, 
            editingMatch.matchIndex, 
            winner, 
            `${s1}-${s2}`
          );
        }
        
        // Update the tournament document
        transaction.update(tournamentRef, {
          brackets: updatedBrackets,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
        
        // Create a match result record
        const matchResultRef = tournamentRef.collection('matchResults').doc();
        transaction.set(matchResultRef, {
          matchId: editingMatch.match.id || `${editingMatch.roundIndex}-${editingMatch.matchIndex}`,
          round: editingMatch.roundIndex + 1,
          team1: editingMatch.match.team1,
          team2: editingMatch.match.team2,
          score1: s1,
          score2: s2,
          winner,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          createdBy: currentUser.uid
        });
        
        return updatedBrackets;
      });
      
      // If a callback function was provided, call it with the updated brackets
      if (onBracketsUpdate && typeof onBracketsUpdate === 'function') {
        onBracketsUpdate();
      }
      
      // Reset the editing state
      setEditingMatch(null);
      setScore1('');
      setScore2('');
    } catch (error) {
      console.error('Error updating match:', error);
      setError(error.message || 'Failed to update match');
    } finally {
      setUpdatingMatch(false);
    }
  };
  
  // Return loading state if data is loading
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-killa-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {error && (
        <div className="bg-red-900/20 border border-red-700 text-red-200 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
        {rounds.map((round, roundIndex) => (
          <div key={`round-${round.round}`} className="space-y-6">
            {/* Round Header */}
            <div className="flex items-center">
              <h3 className="text-lg font-semibold">{round.title}</h3>
              {roundIndex === rounds.length - 1 && (
                <FaTrophy className="ml-2 text-harmony-orange" />
              )}
            </div>
            
            {/* Matches */}
            <div className="space-y-6 relative">
              {round.matches.map((match, matchIndex) => (
                <motion.div
                  key={`match-${match.id || matchIndex}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: matchIndex * 0.1 }}
                  className="bg-killa-background rounded-lg overflow-hidden border border-gray-700"
                >
                  {editingMatch && 
                   editingMatch.roundIndex === roundIndex && 
                   editingMatch.matchIndex === matchIndex ? (
                    // Match editing form
                    <div className="p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <span className="font-bold">Update Match Result</span>
                        <button 
                          onClick={handleCancelEdit}
                          className="text-gray-400 hover:text-gray-200"
                        >
                          <FaTimes />
                        </button>
                      </div>
                      
                      <div className="flex items-center mb-4">
                        <div className="flex-1">
                          <p className="mb-1 text-sm">{getTeamName(match.team1)}</p>
                          <input
                            type="number"
                            min="0"
                            value={score1}
                            onChange={(e) => setScore1(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white"
                          />
                        </div>
                        <span className="mx-2 text-lg">vs</span>
                        <div className="flex-1">
                          <p className="mb-1 text-sm">{getTeamName(match.team2)}</p>
                          <input
                            type="number"
                            min="0"
                            value={score2}
                            onChange={(e) => setScore2(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white"
                          />
                        </div>
                      </div>
                      
                      <button
                        onClick={handleSaveMatch}
                        disabled={updatingMatch}
                        className="w-full bg-harmony-blue hover:bg-harmony-blue-dark text-white rounded py-2 flex items-center justify-center"
                      >
                        {updatingMatch ? (
                          <span className="animate-pulse">Saving...</span>
                        ) : (
                          <>
                            <FaCheck className="mr-1" /> Save Result
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    // Normal match display
                    <>
                      {/* Team 1 */}
                      <div 
                        className={`p-4 flex justify-between items-center border-b border-gray-700 ${
                          match.winner === match.team1 ? 'bg-harmony-blue-dark' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-harmony-blue flex items-center justify-center text-white mr-3">
                            {getTeamName(match.team1).charAt(0)}
                          </div>
                          <span className={`${match.winner === match.team1 ? 'font-semibold text-harmony-white' : ''}`}>
                            {getTeamName(match.team1)}
                          </span>
                        </div>
                        <span className="font-semibold text-harmony-orange">
                          {match.score ? match.score.split('-')[0] : '-'}
                        </span>
                      </div>
                      
                      {/* Team 2 */}
                      <div 
                        className={`p-4 flex justify-between items-center ${
                          match.winner === match.team2 ? 'bg-harmony-blue-dark' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-harmony-red flex items-center justify-center text-white mr-3">
                            {getTeamName(match.team2).charAt(0)}
                          </div>
                          <span className={`${match.winner === match.team2 ? 'font-semibold text-harmony-white' : ''}`}>
                            {getTeamName(match.team2)}
                          </span>
                        </div>
                        <span className="font-semibold text-harmony-orange">
                          {match.score ? match.score.split('-')[1] : '-'}
                        </span>
                      </div>
                      
                      {/* Match Details and Actions */}
                      <div className="bg-gray-800 p-2 text-xs flex justify-between items-center">
                        <div className="text-gray-400">
                          {match.scheduledTime ? new Date(match.scheduledTime.seconds * 1000).toLocaleString() : ''}
                        </div>
                        
                        {/* Edit Button (Only for tournament creators/admins) */}
                        {canEditMatches() && match.team1 && match.team2 && (
                          <button
                            onClick={() => handleEditMatch(match, roundIndex, matchIndex)}
                            className="text-harmony-blue hover:text-harmony-orange transition-colors"
                            title="Edit match result"
                          >
                            <FaEdit />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
              
              {/* Placeholder for empty matches to maintain grid alignment */}
              {round.matches.length === 0 && (
                <div className="h-32 border border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-500">
                  No matches scheduled
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Lines connecting the brackets (only on larger screens) */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          {rounds.map((round, roundIndex) => {
            if (roundIndex < rounds.length - 1) {
              return round.matches.map((match, matchIndex) => {
                const nextRound = rounds[roundIndex + 1];
                const nextMatchIndex = Math.floor(matchIndex / 2);
                
                if (nextRound && nextRound.matches[nextMatchIndex]) {
                  const startY = 120 + matchIndex * 160;
                  const endY = 120 + nextMatchIndex * 160;
                  
                  return (
                    <svg
                      key={`line-${roundIndex}-${matchIndex}`}
                      className="absolute top-0 left-0 w-full h-full"
                      style={{ overflow: 'visible' }}
                    >
                      <path
                        d={`M${(roundIndex + 1) * 25}% ${startY} 
                            H${(roundIndex + 1) * 25 + 12.5}% 
                            V${endY} 
                            H${(roundIndex + 2) * 25}%`}
                        stroke="#4B5563"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  );
                }
                return null;
              });
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default TournamentBracket; 